import { useEffect, useState, useCallback } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { useLiff } from '../../contexts/LiffContext';
import { LiffLoginGate } from '../../components/LiffLoginGate';
import {
  getUserByLineId,
  getUserOrders,
  updateOrderStatus,
  type Order,
} from '../../api/memberClient';
import { Loading } from '../../components/Loading';
import {
  PAYMENT_STATUS,
  SHIPPING_STATUS,
  PICKUP_DEFERRED_PAYMENT_METHOD,
} from '@pos/shared/constants';
import { formatTwdFromCents } from '@pos/shared/money';
import { formatVariantSnapshot } from '@pos/shared/utils';
import { useOrdersGet } from '@pos/api-sdk';
import { getApiHeaders, getTenantIdFromUrl } from '../../api/apiUtils';
import type { OrdersGet200DataOrderItemsItem } from '@pos/api-sdk';
import { ChevronDown, ChevronUp } from 'lucide-react';

function getLoginRedirectUri(): string {
  return (
    window.location.origin + window.location.pathname + window.location.search
  );
}

/** 格式化取貨日為台北時間顯示 */
function formatPickupDate(dateStr: string | null | undefined): string | null {
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return null;
  try {
    return formatInTimeZone(
      new Date(dateStr + 'T12:00:00Z'),
      'Asia/Taipei',
      'yyyy/MM/dd'
    );
  } catch {
    return dateStr;
  }
}

/** 展開時依訂單 ID 取得詳情並顯示購買細項 */
function PickupOrderDetailContent({
  orderId,
  isExpanded,
}: {
  orderId: string;
  isExpanded: boolean;
}) {
  const tenantId = getTenantIdFromUrl();
  const apiHeaders = tenantId ? getApiHeaders(tenantId) : undefined;

  const { data, isLoading } = useOrdersGet(orderId, {
    swr: { enabled: isExpanded && !!tenantId },
    fetch: apiHeaders ? { headers: apiHeaders } : undefined,
  });

  if (!isExpanded) return null;
  if (isLoading) {
    return (
      <div className="py-3">
        <Loading />
      </div>
    );
  }
  const body = data?.data;
  const detail =
    body && typeof body === 'object' && 'data' in body ? body.data : undefined;
  const items: OrdersGet200DataOrderItemsItem[] = detail?.order_items ?? [];
  const totalTwd = detail?.total_twd ?? 0;

  return (
    <div className="text-sm space-y-2 pt-2 border-t border-gray-200 mt-3">
      <div className="font-medium text-gray-700 mb-1">購買細項</div>
      <ul className="space-y-1.5">
        {items.map(item => (
          <li
            key={String(item.id)}
            className="flex justify-between items-start gap-2 text-gray-800"
          >
            <span>
              {item.product_name_snapshot || '商品'}
              {(item as { variant_snapshot?: Record<string, string> | null })
                .variant_snapshot && (
                <span className="text-gray-500 ml-1">
                  ·{' '}
                  {formatVariantSnapshot(
                    (
                      item as {
                        variant_snapshot?: Record<string, string> | null;
                      }
                    ).variant_snapshot
                  )}
                </span>
              )}
              <span className="text-gray-500 ml-1">x {item.quantity}</span>
            </span>
            <span className="shrink-0">
              {formatTwdFromCents(
                item.total_twd ?? item.unit_price_twd * item.quantity
              )}
            </span>
          </li>
        ))}
      </ul>
      <div className="flex justify-between pt-2 border-t border-gray-300 font-semibold text-gray-900">
        <span>小計</span>
        <span>{formatTwdFromCents(totalTwd)}</span>
      </div>
    </div>
  );
}

/** 判斷出貨狀態是否為待取貨（出貨單由後端推導：待處理／待出貨／處理中／已送達 皆可至門市取貨） */
function isWaitingForPickup(status: string | null | undefined): boolean {
  if (!status) return false;
  const waitingStatuses: string[] = [
    SHIPPING_STATUS.PENDING, // 待出貨（舊欄位語意，後端無出貨單時可能回傳）
    '待處理', // 出貨單 pending（對應 SHIPPING_STATUS.PENDING_PROCESSING）
    '處理中', // 出貨單 processing（對應 SHIPPING_STATUS.PROCESSING）
    '已送達', // 出貨單 delivered（對應 SHIPPING_STATUS.DELIVERED，待取貨）
  ];
  return waitingStatuses.includes(status);
}

/**
 * 取貨頁面應用程式
 */
export function PickupApp() {
  const {
    isReady,
    profile,
    error: liffError,
    isLoggedIn,
    shouldUseLiff,
    liff,
  } = useLiff();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingOrders, setPendingOrders] = useState<Order[]>([]);
  const [expandedOrderIds, setExpandedOrderIds] = useState<Set<string>>(
    new Set()
  );
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmingOrderId, setConfirmingOrderId] = useState<number | null>(
    null
  );
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  // 查詢待出貨訂單（多筆一次顯示）
  const fetchPendingOrders = useCallback(async () => {
    if (!profile) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 先透過 LINE ID 取得基本資訊
      const lineIdResponse = await getUserByLineId(profile.userId);

      if (!lineIdResponse.success || !lineIdResponse.data) {
        setError('找不到會員資料，請先註冊');
        setLoading(false);
        return;
      }

      // 取得使用者訂單列表
      const ordersResponse = await getUserOrders(
        lineIdResponse.data.id,
        1,
        100
      );

      if (!ordersResponse.success) {
        setError('查詢訂單失敗');
        setLoading(false);
        return;
      }

      const orders = ordersResponse.data || [];

      // 取貨驗證：面交預購已付款，或面交付款模式未付款待取貨
      const pending = orders.filter(order => {
        const isPreorderConfirmed =
          order.channel === 'preorder' && order.order_status === '成立';
        const isPaid = order.payment_status === PAYMENT_STATUS.PAID;
        const isPickupPayUnpaid =
          order.payment_method === PICKUP_DEFERRED_PAYMENT_METHOD &&
          order.payment_status === PAYMENT_STATUS.NOT_PAID;
        return (
          isPreorderConfirmed &&
          (isPaid || isPickupPayUnpaid) &&
          isWaitingForPickup(order.shipping_status ?? null)
        );
      });

      setPendingOrders(pending);
      setExpandedOrderIds(new Set(pending.map(order => String(order.id))));
      setLoading(false);
    } catch (err) {
      console.error('查詢待出貨訂單失敗:', err);
      setError(err instanceof Error ? err.message : '查詢失敗');
      setLoading(false);
    }
  }, [profile]);

  // 確認取貨（僅面交預購：後端僅更新出貨單為已取貨）
  const handleConfirmPickup = async (order: Order) => {
    setIsConfirming(true);
    setConfirmingOrderId(order.id);
    setError(null);

    try {
      await updateOrderStatus(order.id, {
        confirm_pickup: true,
      });
      setShowSuccessDialog(true);
      await fetchPendingOrders();
    } catch (err) {
      console.error('確認取貨失敗:', err);
      setError(err instanceof Error ? err.message : '確認取貨失敗');
    } finally {
      setIsConfirming(false);
      setConfirmingOrderId(null);
    }
  };

  const toggleOrderExpanded = (orderId: number) => {
    const idStr = String(orderId);
    setExpandedOrderIds(prev => {
      const next = new Set(prev);
      if (next.has(idStr)) next.delete(idStr);
      else next.add(idStr);
      return next;
    });
  };

  // 當 LIFF 準備好時，查詢待出貨訂單
  useEffect(() => {
    if (isReady && isLoggedIn && profile) {
      fetchPendingOrders();
    } else if (isReady && !isLoggedIn) {
      setError('請先登入 LINE');
      setLoading(false);
    }
  }, [isReady, isLoggedIn, profile, fetchPendingOrders]);

  return (
    <LiffLoginGate
      isReady={isReady}
      shouldUseLiff={shouldUseLiff}
      isLoggedIn={isLoggedIn}
      error={liffError}
      onLoginClick={() => liff?.login?.({ redirectUri: getLoginRedirectUri() })}
    >
      {loading ? (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
            <Loading />
            <p className="text-gray-600 mt-4">正在查詢訂單資訊...</p>
          </div>
        </div>
      ) : error ? (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center space-y-4 max-w-md w-full">
            <div className="text-red-600 text-5xl">✗</div>
            <h2 className="text-xl font-bold text-gray-900">查詢失敗</h2>
            <p className="text-sm text-gray-600">{error}</p>
          </div>
        </div>
      ) : showSuccessDialog ? (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center space-y-4 max-w-md w-full">
            <div className="text-green-600 text-5xl">✓</div>
            <h2 className="text-xl font-bold text-gray-900">取貨完成</h2>
            <p className="text-sm text-gray-600">
              您的訂單已確認取貨，感謝您的使用！
            </p>
            <button
              type="button"
              onClick={() => {
                setShowSuccessDialog(false);
                fetchPendingOrders();
              }}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors"
            >
              確定
            </button>
          </div>
        </div>
      ) : pendingOrders.length === 0 ? (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center space-y-4 max-w-md w-full">
            <div className="text-gray-400 text-5xl">📦</div>
            <h2 className="text-xl font-bold text-gray-900">
              目前沒有待取貨訂單
            </h2>
            <p className="text-sm text-gray-600">
              取貨驗證僅處理面交預購訂單，您目前沒有面交預購待取貨的訂單
            </p>
          </div>
        </div>
      ) : (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
          <div className="w-full max-w-md space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                待取貨訂單
              </h2>
              <p className="text-sm text-gray-600">
                請選擇要取貨的訂單並確認資訊
              </p>
            </div>

            <div className="space-y-4">
              {pendingOrders.map(order => {
                const orderIdStr = String(order.id);
                const isExpanded = expandedOrderIds.has(orderIdStr);
                const pickupDateStr = formatPickupDate(order.pickup_date);
                const isThisConfirming =
                  isConfirming && confirmingOrderId === order.id;

                return (
                  <div
                    key={order.id}
                    className="bg-white rounded-2xl shadow-lg p-6 space-y-4"
                  >
                    {/* 取貨日期／取貨號碼共用灰底 */}
                    {(pickupDateStr || order.pickup_number) && (
                      <div className="rounded-lg bg-slate-100 px-3 py-2 space-y-1 text-sm font-semibold text-slate-800">
                        {pickupDateStr && <div>取貨日期：{pickupDateStr}</div>}
                        {order.pickup_number && (
                          <div>取貨號碼：{order.pickup_number}</div>
                        )}
                      </div>
                    )}

                    {/* 外帶號碼（若有） */}
                    {order.takeaway_number != null && (
                      <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-center text-white">
                        <div className="text-sm font-medium mb-1 opacity-90">
                          外帶號碼
                        </div>
                        <div className="text-3xl font-bold">
                          {order.takeaway_number}
                        </div>
                      </div>
                    )}

                    {/* 訂單摘要：可點擊收合/展開購買細項 */}
                    <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-gray-600 shrink-0">訂單編號</span>
                        <span className="font-medium text-gray-900 whitespace-nowrap min-w-0 truncate text-right">
                          {order.order_number}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">訂單金額</span>
                        <span className="font-medium text-gray-900">
                          {formatTwdFromCents(order.total_twd)}
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={() => toggleOrderExpanded(order.id)}
                        className="flex items-center gap-2 w-full text-left text-sm text-blue-600 hover:text-blue-700 font-medium mt-2"
                      >
                        {isExpanded ? (
                          <>
                            收起購買細項{' '}
                            <ChevronUp className="w-4 h-4 shrink-0" />
                          </>
                        ) : (
                          <>
                            查看購買細項{' '}
                            <ChevronDown className="w-4 h-4 shrink-0" />
                          </>
                        )}
                      </button>

                      <PickupOrderDetailContent
                        orderId={orderIdStr}
                        isExpanded={isExpanded}
                      />
                    </div>

                    <button
                      type="button"
                      onClick={() => handleConfirmPickup(order)}
                      disabled={isConfirming}
                      className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-3 px-4 rounded-lg transition-colors"
                    >
                      {isThisConfirming
                        ? '確認中...'
                        : order.payment_status === PAYMENT_STATUS.NOT_PAID &&
                            order.payment_method ===
                              PICKUP_DEFERRED_PAYMENT_METHOD
                          ? '確認取貨並付款'
                          : '確認取貨'}
                    </button>
                  </div>
                );
              })}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </LiffLoginGate>
  );
}
